/**
 * THE WORKSPACE'S FIVE PLACES — and, inside each, the views that place owns.
 *
 * THE OWNER'S DECISION, MADE STRUCTURAL. Main Chat is the product; the other
 * four areas are where its records live. There are exactly five primary
 * destinations in the navigation, and every one of the thirteen views the
 * workspace already had is reachable as a TAB inside its area. The old
 * thirteen-item list was the same product with its filing cabinet tipped onto
 * the floor: every view was offered at the same weight, so none of them was.
 *
 * NOTHING WAS DELETED. `AREA_TABS` is the total of every surface that existed
 * before this table did, plus Settings (where installation and the session
 * live). A view that is not named here would be unreachable, so the browser
 * acceptance reads this table and walks it rather than trusting a list.
 *
 * THE AREA ID IS THE FIRST PATH SEGMENT; THE TAB ID IS THE SECOND. That is the
 * same grammar the workspace always used, and every old address is translated
 * to its new one in `paths.ts` — deliberately, with the record id preserved,
 * so a notification minted before this change still opens the record it named.
 */

export type DestinationState =
	/** Real: it reads live Scout data through the gateway and renders it. */
	| 'built'
	/** A numbered implementation slice owes this view its real contents. */
	| 'pending';

export interface Destination {
	/** The URL segment. Stable, because a notification's deep link names it. */
	id: string;
	label: string;
	/** One line, shown in the sidebar and as the area's own description. */
	blurb: string;
	state: DestinationState;
	/** THE SIDEBAR'S GROUPING LABEL. The navigation is one list, but seven
	 *  areas at one weight is the filing-cabinet problem again; the groups say
	 *  which places answer the same kind of question. */
	group?: string;
	/** WHICH SLICE BUILDS IT, for the pending ones. Named rather than implied,
	 *  so "not built" is a plan and not a shrug. */
	slice?: number;
	/** THE GATEWAY ROUTES THIS VIEW WILL READ. Named from the real route table,
	 *  so a reader can check the claim rather than trust it. */
	reads?: readonly string[];
	/** Shown on the phone's bottom bar. Four areas plus a "More" control fit a
	 *  thumb's reach; every area remains one press away in that sheet. */
	inBottomBar?: boolean;
	/** The bar's shorter name where the full one does not fit. It is a name,
	 *  not an abbreviation — "History" under a thumb and "History & Search" at
	 *  the head of the page are one place. */
	barLabel?: string;
}

/** One view inside an area. `section` is the second path segment; `null` is
 *  the area's own default view. */
export interface AreaTab {
	id: string;
	label: string;
	section: string | null;
	/** THE VIEW'S OWN `<h1>` WHERE IT DIFFERS FROM THE TAB'S SHORT NAME. A tab
	 *  is a thumb-sized word; the page it opens may have a longer true title. */
	heading?: string;
	/** One line, for the tab's title attribute and the area's help. */
	blurb: string;
}

export interface AreaDestination extends Destination {
	tabs: readonly AreaTab[];
}

/**
 * THE SEVEN AREAS, IN THE OWNER'S RESEARCH-PLATFORM ORDER.
 *
 * `reads` IS DELIBERATELY EMPTY ON THE AREAS AND LIVES ON THE TABS. An area is
 * a place, not a read; claiming routes on it would repeat the drift the old
 * table accumulated (a route listed on a destination that no view called).
 */
export const DESTINATIONS: readonly AreaDestination[] = [
	{
		id: 'chat',
		label: 'Main Chat',
		barLabel: 'Chat',
		blurb: 'The one conversation — where research is started and everything is discussed.',
		state: 'built',
		group: 'Work',
		inBottomBar: true,
		tabs: [],
	},
	{
		id: 'research',
		label: 'Research Control',
		barLabel: 'Research',
		blurb: 'The subjects Scout is investigating, the windows you authorized, and what each window did.',
		state: 'built',
		group: 'Research',
		inBottomBar: true,
		tabs: [
			{
				id: 'topics',
				label: 'Topics',
				section: null,
				heading: 'Research Control',
				blurb: 'Every research topic, its workspace, its status, and what it holds.',
			},
			{
				id: 'windows',
				label: 'Windows',
				section: 'windows',
				heading: 'Research windows',
				blurb: 'Authorizations, deadlines, execution facts, and the stop that is real.',
			},
			{
				id: 'missions',
				label: 'Missions',
				section: 'missions',
				blurb: 'The Owner-authored research mandate: question, deadline, public search plan, continue and pause.',
			},
			{
				id: 'observatory',
				label: 'Observatory',
				section: 'observatory',
				blurb: 'What Scout investigated, what it read, and what it could not settle.',
			},
			{
				id: 'experiments',
				label: 'Experiments',
				section: 'experiments',
				blurb: 'What Scout proposed to test, what the sandbox did, and what the result changed.',
			},
		],
	},
	{
		id: 'spaces',
		label: 'Research Workspaces',
		barLabel: 'Spaces',
		blurb: 'Domain programmes and the research identities inside them.',
		state: 'built',
		group: 'Research',
		inBottomBar: true,
		tabs: [],
	},
	{
		id: 'discoveries',
		label: 'Discoveries',
		barLabel: 'Discoveries',
		blurb: 'The evidence behind the conversation: sources, knowledge, and generated media.',
		state: 'built',
		group: 'Knowledge',
		inBottomBar: false,
		tabs: [
			{
				id: 'evidence',
				label: 'Evidence',
				section: 'evidence',
				blurb: 'A claim, the citation that names it, and what that citation resolves to.',
			},
			{
				id: 'knowledge',
				label: 'Knowledge',
				section: 'knowledge',
				blurb: 'What Scout currently holds, what the evidence went against, and what remains open.',
			},
			{
				id: 'media',
				label: 'Media',
				section: 'media',
				blurb: 'The images and speech Scout generated, with the provider, model and digest the store recorded.',
			},
		],
	},
	{
		id: 'foundry',
		label: 'Capability Library',
		barLabel: 'Library',
		blurb: 'Platform instruments and Foundry capabilities, with their origin, availability and execution record.',
		state: 'built',
		group: 'Knowledge',
		inBottomBar: true,
		tabs: [
			{
				id: 'library',
				label: 'Library',
				section: null,
				heading: 'Capability Library',
				blurb: 'One searchable inventory: what is built in, what was registered, and what can actually run.',
			},
		],
	},
	{
		id: 'history',
		label: 'History & Search',
		barLabel: 'History',
		blurb: 'Earlier conversations, research that ran, and one search across every record.',
		state: 'built',
		group: 'Knowledge',
		inBottomBar: false,
		tabs: [
			{
				id: 'conversations',
				label: 'Conversations',
				section: null,
				heading: 'History & Search',
				blurb: 'Research windows, the scheduler’s work, and every earlier conversation.',
			},
			{
				id: 'search',
				label: 'Search',
				section: 'search',
				blurb: 'One query across conversations, research, evidence, knowledge and improvements.',
			},
		],
	},
	{
		id: 'workspace',
		label: 'System',
		barLabel: 'System',
		blurb: 'How Scout is doing, what it costs, what needs you, and this device.',
		state: 'built',
		group: 'Operations',
		inBottomBar: false,
		tabs: [
			{
				id: 'overview',
				label: 'Overview',
				section: null,
				heading: 'System overview',
				blurb: 'Whether Scout is answering, what is running, and what needs you.',
			},
			{
				id: 'improvements',
				label: 'Improvements',
				section: 'improvements',
				heading: 'Improvements',
				blurb: 'Scout’s proposals and critiques, with the Owner’s review and evidence trail.',
			},
			{
				id: 'status',
				label: 'Status',
				section: 'status',
				blurb: 'Every capability this build declares, and how far this deployment got with each.',
			},
			{
				id: 'resources',
				label: 'Resources',
				section: 'resources',
				blurb: 'What Scout spent, on what, with its provenance — and what is unknown.',
			},
			{
				id: 'notifications',
				label: 'Notifications',
				section: 'notifications',
				blurb: 'The durable inbox, and the records its entries point at.',
			},
			{
				id: 'settings',
				label: 'Settings',
				section: 'settings',
				blurb: 'Install Scout on this device, and end this browser’s session.',
			},
		],
	},
];

/** Where the workspace opens — THE CONVERSATION, which is the product's whole
 *  organising idea. Everything else is a view beside it. */
export const DEFAULT_DESTINATION = 'chat';

/** Kept as a type alias so the views that predate the five areas still name
 *  what they render without a second vocabulary. */
export type ViewId =
	| 'chat'
	| 'research'
	| 'observatory'
	| 'evidence'
	| 'knowledge'
	| 'media'
	| 'experiments'
	| 'improvements'
	| 'history'
	| 'search'
	| 'notifications'
	| 'resources'
	| 'status'
	| 'settings';

export function destinationById(id: string): AreaDestination | undefined {
	return DESTINATIONS.find((destination) => destination.id === id);
}

export function tabById(areaId: string, tabId: string): AreaTab | undefined {
	return destinationById(areaId)?.tabs.find((tab) => tab.id === tabId);
}

/**
 * WHICH TAB A ROUTE IS ON. The route's second segment is the tab's own id for
 * most areas, but several tabs carry a deeper address — the Observatory's
 * episode, an experiment's receipt, a charter, a seated candidate. This
 * function is the ONE place that mapping lives, so the tab strip and the view
 * switch cannot disagree.
 */
export function areaTabFor(destinationId: string, section: string | null): AreaTab | null {
	const area = destinationById(destinationId);
	if (area === undefined || area.tabs.length === 0) return null;
	const found = area.tabs.find((tab) => tab.section !== null && tab.section === section);
	if (found !== undefined) return found;
	// ADDRESSES THAT BELONG TO A TAB WITHOUT NAMING IT. Each is a record
	// inside the tab, and each was a real deep link before the areas existed.
	if (destinationId === 'research') {
		if (section === 'window') return area.tabs.find((tab) => tab.id === 'windows') ?? null;
		// A RESEARCH TOPIC IS A PAGE UNDER TOPICS, not a sixth tab. The tab
		// strip stays on Topics while the page is the topic's own.
		if (section === 'topic') return area.tabs.find((tab) => tab.id === 'topics') ?? null;
	}
	if (destinationId === 'foundry' && section === 'capability') {
		return area.tabs.find((tab) => tab.id === 'library') ?? null;
	}
	if (destinationId === 'workspace') {
		if (section === 'charter' || section === 'decisions' || section === 'decision') {
			return area.tabs.find((tab) => tab.id === 'improvements') ?? null;
		}
		if (section === 'improvements') return area.tabs.find((tab) => tab.id === 'improvements') ?? null;
		// `/workspace/overview` is the old Home address; the Overview tab now
		// owns the area's bare address, so the named address still selects it.
		if (section === 'overview') return area.tabs.find((tab) => tab.id === 'overview') ?? null;
	}
	if (destinationId === 'history' && section === 'conversation') {
		return area.tabs.find((tab) => tab.id === 'conversations') ?? null;
	}
	// THE AREA'S OWN ADDRESS (`/research`, `/history`, …) IS ITS FIRST TAB.
	if (section === null) return area.tabs[0] ?? null;
	return null;
}
